const SEVERITY_RANK: Record<string, number> = { critical: 4, high: 3, medium: 2, med: 2, warn: 2, low: 1 };

export function riskLevelRank(level?: string): number {
  if (!level) return 0;
  return SEVERITY_RANK[level.toLowerCase()] ?? 0;
}

export function riskLevelFromRank(rank: number): string {
  if (rank >= 4) return 'critical';
  if (rank >= 3) return 'high';
  if (rank >= 2) return 'medium';
  return 'low';
}

/** Infer per-factor severity from review / critique prose — not only overall level. */
export function inferSeverityFromRiskText(text: string): string | undefined {
  const d = (text || '').toLowerCase();
  if (
    d.includes('patch.files is an empty') ||
    d.includes('empty array') ||
    d.includes('never materialized') ||
    d.includes('impossible to verify') ||
    (d.includes('confidence') && d.includes('0'))
  ) {
    return 'high';
  }
  if (d.includes('blocking') || d.includes('reject')) {
    if (d.includes('remediation critique') && d.includes('flagged blocking')) {
      return 'medium';
    }
    return 'high';
  }
  if (d.includes('zero runtime') || d.includes('idempotency') || d.includes('outside this path')) {
    return 'medium';
  }
  return undefined;
}

export function highestRiskLevel(levels: (string | undefined)[]): string | undefined {
  let best: string | undefined;
  let bestRank = -1;
  for (const l of levels) {
    if (!l) continue;
    const r = riskLevelRank(l);
    if (r > bestRank) {
      bestRank = r;
      best = l;
    }
  }
  return best;
}

export type RiskSummaryTone = 'critical' | 'warn' | 'neutral' | 'info';

export function riskSummaryTone(level?: string): RiskSummaryTone {
  const r = riskLevelRank(level);
  if (r >= 3) return 'critical';
  if (r >= 2) return 'warn';
  if (r >= 1) return 'neutral';
  return 'info';
}

export interface UnifiedRiskSummary {
  eiLevel?: string;
  governanceTier?: string;
  governanceScore?: number;
  /** The sealed patch's authoritative blast radius level, "unknown" included. */
  blastRadiusLevel?: string;
  headline: string;
  /** Highest graded level; "unknown" when the only signal is an ungraded
   * blast radius, undefined when nothing was graded — never a default "low". */
  effectiveLevel?: string;
  tone: RiskSummaryTone;
  /** Signals that raise no grade but must stay visible, e.g. a blast radius
   * nobody could compute. */
  factors: string[];
}

export function buildUnifiedRiskSummary(input: {
  eiLevel?: string;
  factorSeverities?: string[];
  governanceTier?: string;
  governanceScore?: number;
  /** Authoritative blast radius level (low..critical, unknown, not_applicable). */
  blastRadiusLevel?: string;
  /** Why the blast radius is unknown, when it is. */
  blastRadiusReason?: string;
}): UnifiedRiskSummary {
  const eiEffective = highestRiskLevel([input.eiLevel, ...input.factorSeverities ?? []]);
  const blast = (input.blastRadiusLevel || '').trim().toLowerCase();
  const blastRank = riskLevelRank(blast);
  const blastUnknown = blast === 'unknown';
  const govRank = riskLevelRank(input.governanceTier);
  const eiRank = riskLevelRank(eiEffective);
  const effectiveRank = Math.max(eiRank, govRank, blastRank);
  const effectiveLevel = effectiveRank > 0 ? riskLevelFromRank(effectiveRank) : blastUnknown ? 'unknown' : undefined;

  const parts: string[] = [];
  if (eiEffective) {
    parts.push(`EI ${eiEffective}`);
  }
  if (blastRank > 0 || blastUnknown) {
    parts.push(`Blast radius ${blast}`);
  }
  if (input.governanceTier) {
    const score =
      input.governanceScore != null && !Number.isNaN(input.governanceScore)
        ? ` (${Math.round(input.governanceScore)})`
        : '';
    parts.push(`Governance ${input.governanceTier}${score}`);
  }

  const headline =
    parts.length > 0
      ? parts.join(' · ')
      : eiEffective
        ? eiEffective.toUpperCase()
        : 'None flagged';

  const factors: string[] = [];
  if (blastUnknown) {
    factors.push(
      `Blast radius unknown — ${input.blastRadiusReason?.trim() || 'the change could not be traced through the repository'}`,
    );
  }

  // An ungraded blast radius needs a reviewer's eyes even when every graded
  // signal is low, so it never reads calmer than a warning.
  let tone = riskSummaryTone(effectiveLevel);
  if (blastUnknown && (tone === 'info' || tone === 'neutral')) tone = 'warn';

  return {
    eiLevel: eiEffective,
    governanceTier: input.governanceTier,
    governanceScore: input.governanceScore,
    blastRadiusLevel: blast || undefined,
    headline,
    effectiveLevel,
    tone,
    factors,
  };
}
